// Deterministic, DOM-free transformations applied to composition HTML before it loads into the iframe.

import type { HtmlFontFace } from './htmlFonts';

export const TEMPLATE_COLOR_STYLE_ID = 'l5-template-colors';

/** Id of the injected style carrying the `--l5-font-N` variables and their `@font-face` rules. */
export const HTML_FONT_STYLE_ID = 'l5-fonts';

/** Id of the injected style that neutralizes author rules landing on the capture's root `<svg>`. */
export const CAPTURE_ROOT_STYLE_ID = 'l5-capture-root';

/** Prefix of the injected font custom properties (e.g. `--l5-font-0`), indexed like the template `fonts`. */
export const TEMPLATE_FONT_VAR_PREFIX = '--l5-font-';

/** Id of the single inline `<script>` carrying the vendored gsap core and curated plugins. */
export const GSAP_SCRIPT_ID = 'l5-gsap';

/** The gsap version vendored into every composition. Kept in step with the `gsap` dependency. */
export const GSAP_VERSION = '3.13.0';

// Order matters: CustomEase builds the eases CustomBounce and CustomWiggle extend. The scroll and
// input plugins are omitted on purpose — a composition is rasterized by seeking a paused timeline,
// with no scroll position, pointer or wall clock for them to read. This list is the source of truth
// the inline-source generator (scripts/generate-gsap-inline.mjs) mirrors.
export const GSAP_PLUGINS = [
    'CustomEase',
    'CustomBounce',
    'CustomWiggle',
    'DrawSVGPlugin',
    'EasePack',
    'MorphSVGPlugin',
    'MotionPathPlugin',
    'Physics2DPlugin',
    'ScrambleTextPlugin',
    'SplitText',
    'TextPlugin',
] as const;

/** Prefix of the injected palette custom properties (e.g. `--l5-color-0`), indexed like the template `colors`. */
export const TEMPLATE_COLOR_VAR_PREFIX = '--l5-color-';

/** The `var(...)` reference an author uses to read template color `index`. */
export function htmlTemplateColorVar(index: number): string {
    return `var(${TEMPLATE_COLOR_VAR_PREFIX}${index})`;
}

export function templateColorToHex(color: number): string {
    return '#' + ((color >>> 0) & 0xffffff).toString(16).padStart(6, '0');
}

export function buildTemplateColorCss(palette: string[]): string {
    const declarations = palette
        .map((color, index) => `${TEMPLATE_COLOR_VAR_PREFIX}${index}: ${color};`)
        .join(' ');
    return `:root { ${declarations} }`;
}

/** Insert or replace the template-color `<style>` block — idempotent (swaps rather than appends). */
export function injectTemplateColorVariables(html: string, palette: string[]): string {
    const styleTag = `<style id="${TEMPLATE_COLOR_STYLE_ID}">${buildTemplateColorCss(palette)}</style>`;

    const existingBlock = new RegExp(
        `<style id="${TEMPLATE_COLOR_STYLE_ID}">[\\s\\S]*?</style>`,
        'i',
    );
    const withoutExisting = html.replace(existingBlock, '');

    if (/<\/head>/i.test(withoutExisting)) {
        return withoutExisting.replace(/<\/head>/i, `${styleTag}</head>`);
    }
    if (/<head[^>]*>/i.test(withoutExisting)) {
        return withoutExisting.replace(/<head[^>]*>/i, (match) => `${match}${styleTag}`);
    }
    return `${styleTag}${withoutExisting}`;
}

/**
 * The `--l5-font-N` variable declarations plus a matching `@font-face` for each font. The variable
 * carries the family name so the composition references the font indirectly (`var(--l5-font-N)`)
 * and follows template-font changes — the same indirection the palette uses. Faces are data URIs
 * so both the iframe layout and snapDOM's SVG-image rasterization can use them (an SVG-as-image
 * can't fetch external font URLs).
 */
export function buildFontCss(fonts: HtmlFontFace[]): string {
    const declarations = fonts
        .map((font) => `${TEMPLATE_FONT_VAR_PREFIX}${font.index}: '${font.familyName}';`)
        .join(' ');
    // Different slots can resolve to the same font; emit each unique family's @font-face once so its
    // (large) bytes aren't embedded — and re-serialized on every capture — more than once.
    const seenFamilies = new Set<string>();
    const faces = fonts
        .filter((font) => {
            if (seenFamilies.has(font.familyName)) {
                return false;
            }
            seenFamilies.add(font.familyName);
            return true;
        })
        .map(
            (font) =>
                `@font-face { font-family: '${font.familyName}'; src: url(${font.dataUri}); }`,
        )
        .join(' ');
    return `:root { ${declarations} } ${faces}`;
}

/** Insert or replace the template-font `<style>` block (vars + faces) — idempotent. */
export function injectFonts(html: string, fonts: HtmlFontFace[]): string {
    const existingBlock = new RegExp(`<style id="${HTML_FONT_STYLE_ID}">[\\s\\S]*?</style>`, 'i');
    const withoutExisting = html.replace(existingBlock, '');
    if (!fonts.length) {
        return withoutExisting;
    }
    const styleTag = `<style id="${HTML_FONT_STYLE_ID}">${buildFontCss(fonts)}</style>`;

    if (/<\/head>/i.test(withoutExisting)) {
        return withoutExisting.replace(/<\/head>/i, `${styleTag}</head>`);
    }
    if (/<head[^>]*>/i.test(withoutExisting)) {
        return withoutExisting.replace(/<head[^>]*>/i, (match) => `${match}${styleTag}`);
    }
    return `${styleTag}${withoutExisting}`;
}

/**
 * Neutralize the positioning properties on the capture's own root `<svg>`.
 *
 * snapDOM rasterizes by serializing the composition as a standalone SVG document — a root `<svg>`
 * wrapping a `<foreignObject>` clone of the document — and it keeps the composition's `<style>`
 * blocks (which is how the injected data-URI `@font-face` rules reach the raster). CSS in an SVG
 * document applies document-wide, so an author rule written as a bare type selector,
 * `svg { position: absolute; left: 520px; top: 680px }`, also matches that root and translates the
 * whole rasterized frame — which then composites into the scene visibly offset. `svg:root` matches
 * nothing in the live composition, whose root is `<html>`, so this only ever bites at capture time.
 */
export function injectCaptureRootGuard(html: string): string {
    const styleTag =
        `<style id="${CAPTURE_ROOT_STYLE_ID}">svg:root{position:static!important;` +
        `inset:auto!important;left:auto!important;top:auto!important;right:auto!important;` +
        `bottom:auto!important;margin:0!important;float:none!important;transform:none!important;` +
        `translate:none!important;rotate:none!important;scale:none!important}</style>`;

    const existingBlock = new RegExp(
        `<style id="${CAPTURE_ROOT_STYLE_ID}">[\\s\\S]*?</style>`,
        'i',
    );
    const withoutExisting = html.replace(existingBlock, '');

    if (/<\/head>/i.test(withoutExisting)) {
        return withoutExisting.replace(/<\/head>/i, `${styleTag}</head>`);
    }
    if (/<head[^>]*>/i.test(withoutExisting)) {
        return withoutExisting.replace(/<head[^>]*>/i, (match) => `${match}${styleTag}`);
    }
    return `${styleTag}${withoutExisting}`;
}

export function hasTimelineContract(html: string): boolean {
    return /__timelines/.test(html);
}

/** Id of the injected style that hides editable glyphs during natural text editing. */
export const NTE_EDITING_STYLE_ID = 'l5-nte-editing';

function escapeCssAttributeValue(value: string): string {
    return value.replace(/[\\"<>]/g, (char) => `\\${char.codePointAt(0)!.toString(16)} `);
}

/**
 * Hide the rasterized glyphs of the active editable region (layout preserved) so the editor's live
 * overlay is the only visible copy of that node. Injected only while editing.
 */
export function injectEditingHideStyle(html: string, activeRegionId: string): string {
    const selector = `[data-l5-editable="true"][id="${escapeCssAttributeValue(activeRegionId)}"]`;
    const styleTag = `<style id="${NTE_EDITING_STYLE_ID}">${selector} { color: transparent !important; }</style>`;
    if (/<\/head>/i.test(html)) {
        return html.replace(/<\/head>/i, `${styleTag}</head>`);
    }
    if (/<head[^>]*>/i.test(html)) {
        return html.replace(/<head[^>]*>/i, (match) => `${match}${styleTag}`);
    }
    return `${styleTag}${html}`;
}

export interface ManageCompositionOptions {
    /** Id of the region being edited; when set, its raster glyphs are hidden. */
    editingRegionId?: string | null;
    /** Template fonts to inject as data-URI `@font-face` rules. */
    fonts?: HtmlFontFace[];
}

export function manageComposition(
    html: string,
    palette: string[],
    options: ManageCompositionOptions = {},
): string {
    const withColors = injectTemplateColorVariables(html, palette);
    const withGuard = injectCaptureRootGuard(withColors);
    const withFonts = injectFonts(withGuard, options.fonts ?? []);
    return options.editingRegionId
        ? injectEditingHideStyle(withFonts, options.editingRegionId)
        : withFonts;
}
